import { Card, CardBody, CardHeader, Chip, SortDescriptor, Switch } from '@nextui-org/react'
import React, { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button } from '~/components/common/Button'
import { CustomTooltip } from '~/components/common/CustomTooltip'
import { Column, DataTable } from '~/components/common/DataTable'
import { Input } from '~/components/common/Input'
import { Modal, ModalBody, ModalFooter, useModal } from '~/components/modals/CurieModal'
import { SimpleTextarea } from '~/components/common/SimpleTextarea'
import {
  BillingAccountData,
  BillingMember,
  BillingWorkspaceDetails,
  bulkInviteBillingMembers,
  cancelBillingInvitation,
  createBillingAccount,
  createBillingWorkspace,
  deleteBillingAccount,
  deleteBillingWorkspace,
  getBillingAccounts,
  getBillingMembers,
  getBillingWorkspacesDetails,
  getWorkspaceMembers,
  removeBillingMember,
  removeWorkspaceMember,
  resendBillingInvitation,
  updateBillingAccount,
  updateBillingWorkspace,
  WorkspaceMember
} from '~/services/superAdminService'
import { formatDate } from '~/utils/timezone'
import { BetterToast } from '~/utils/toast'
import { CreditsModal } from './AccountManagementCreditsModal'
import { LicensesModal } from './AccountManagementLicensesModal'
import { SuperAdminManageAccountDialog } from './SuperAdminManageAccountDialog'

interface FormData {
  name: string
  note: string
}

const initialFormData: FormData = {
  name: '',
  note: ''
}

// Detail Modal for showing account metadata
const DetailModal = ({
  isOpen,
  onClose,
  account
}: {
  isOpen: boolean
  onClose: () => void
  account: BillingAccountData | null
}) => {
  if (!account) return null

  const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div className='flex py-2 border-b last:border-0'>
      <div className='w-1/3 text-sm font-medium text-gray-500'>{label}</div>
      <div className='w-2/3 text-sm text-gray-900'>{value}</div>
    </div>
  )

  return (
    <Modal isOpen={isOpen} onClose={onClose} hideCloseButton={false}>
      <ModalBody>
        <h3 className='text-lg font-semibold mb-4'>Account Details - {account.name}</h3>

        <div className='space-y-1'>
          <DetailRow label='ID' value={<span className='font-mono'>#{account.id}</span>} />
          <DetailRow label='Name' value={account.name || '-'} />
          <DetailRow label='Note' value={account.note || '-'} />
          <DetailRow label='Billing Email' value={account.billing_email || '-'} />
          <DetailRow label='Billing To' value={account.billing_to || '-'} />
          <DetailRow
            label='Payment Method'
            value={
              account.has_payment_method ? (
                <Chip size='sm' variant='flat' color='success'>
                  Set up
                </Chip>
              ) : (
                <Chip size='sm' variant='flat' color='warning'>
                  Not set
                </Chip>
              )
            }
          />
          <DetailRow label='Created' value={formatDate(account.created_time)} />
          <DetailRow label='Updated' value={formatDate(account.updated_time)} />
        </div>

        {/* Billing Metadata Section */}
        {account.billing_metadata && Object.keys(account.billing_metadata).length > 0 && (
          <div className='mt-4'>
            <h4 className='text-sm font-semibold mb-2 text-gray-700'>Billing Metadata</h4>
            <div className='bg-gray-50 rounded-lg p-3 max-h-[200px] overflow-auto'>
              <pre className='text-xs font-mono whitespace-pre-wrap break-all'>
                {JSON.stringify(account.billing_metadata, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant='outlined' onClick={onClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  )
}

// Separate component for Member Management to handle its own state
const MembersModal = ({
  isOpen,
  onClose,
  account
}: {
  isOpen: boolean
  onClose: () => void
  account: BillingAccountData | null
}) => {
  const [members, setMembers] = useState<BillingMember[]>([])
  const [workspaces, setWorkspaces] = useState<BillingWorkspaceDetails[]>([])
  const [isLoading, setIsLoading] = useState(false)

  // Enhanced invite state
  const [inviteEmails, setInviteEmails] = useState<string[]>([])
  const [inviteEmailInput, setInviteEmailInput] = useState('')
  const [inviteMode, setInviteMode] = useState<'admin' | 'all' | 'workspaces'>('admin')
  const [selectedWorkspaces, setSelectedWorkspaces] = useState<number[]>([])
  const [isInviting, setIsInviting] = useState(false)
  const [inviteSuccess, setInviteSuccess] = useState(false)
  const [workspaceSelectorOpen, setWorkspaceSelectorOpen] = useState(false)
  const inviteInputRef = React.useRef<HTMLInputElement>(null)

  const fetchMembers = useCallback(async () => {
    if (!account) return
    try {
      setIsLoading(true)
      const [membersData, workspacesData] = await Promise.all([
        getBillingMembers(account.id),
        getBillingWorkspacesDetails(account.id)
      ])
      setMembers(membersData)
      setWorkspaces(workspacesData)
    } catch {
      BetterToast.error('Failed to fetch members')
    } finally {
      setIsLoading(false)
    }
  }, [account])

  useEffect(() => {
    if (isOpen && account) {
      fetchMembers()
      // Reset invite state
      setInviteEmails([])
      setInviteEmailInput('')
      setInviteMode('all')
      setSelectedWorkspaces([])
      setInviteSuccess(false)
      setWorkspaceSelectorOpen(false)
    }
  }, [isOpen, account, fetchMembers])

  const handleBulkInvite = async () => {
    if (!account || inviteEmails.length === 0 || inviteSuccess) return
    setIsInviting(true)
    try {
      // Determine workspace_ids based on invite mode
      let workspaceIds: number[] | undefined
      if (inviteMode === 'workspaces' && selectedWorkspaces.length > 0) {
        workspaceIds = selectedWorkspaces
      } else if (inviteMode === 'all') {
        // All workspaces - don't send workspace_ids, backend handles it
        workspaceIds = undefined
      }

      const result = await bulkInviteBillingMembers(account.id, {
        emails: inviteEmails,
        workspace_ids: workspaceIds,
        as_account_admin: inviteMode === 'admin'
      })
      if (result.invited.length > 0) {
        BetterToast.success(`Invitation sent to ${result.invited.length} user(s)`)
        setInviteSuccess(true)
        await fetchMembers()
      }
      if (result.failed.length > 0) {
        BetterToast.error(`Failed to invite: ${result.failed.join(', ')}`)
      }
    } catch (error: unknown) {
      BetterToast.error(error instanceof Error ? error.message : 'Failed to send invitations')
    } finally {
      setIsInviting(false)
    }
  }

  const handleRemoveMember = async (userId: number) => {
    if (!account) return
    try {
      await removeBillingMember(account.id, userId)
      BetterToast.success('Member removed successfully')
      await fetchMembers()
    } catch (error: unknown) {
      BetterToast.error(error instanceof Error ? error.message : 'Failed to remove member')
    }
  }

  const handleCancelInvitation = async (invitationId: number) => {
    if (!account) return
    try {
      await cancelBillingInvitation(account.id, invitationId)
      BetterToast.success('Invitation cancelled')
      await fetchMembers()
    } catch (error: unknown) {
      BetterToast.error(error instanceof Error ? error.message : 'Failed to cancel invitation')
    }
  }

  const handleResendInvitation = async (invitationId: number) => {
    if (!account) return
    try {
      await resendBillingInvitation(account.id, invitationId)
      BetterToast.success('Invitation resent')
    } catch (error: unknown) {
      BetterToast.error(error instanceof Error ? error.message : 'Failed to resend invitation')
    }
  }

  const getWorkspaceDisplayText = () => {
    if (inviteMode === 'admin') return 'Account Admin'
    if (inviteMode === 'all') return 'All Workspaces'
    if (selectedWorkspaces.length === 0) return 'Select Workspaces'
    return selectedWorkspaces
      .map(id => workspaces.find(ws => ws.org_id === id)?.org_name || '')
      .filter(Boolean)
      .join(', ')
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} hideCloseButton={false} className='max-w-2xl'>
      <ModalBody>
        <h3 className='text-lg font-semibold mb-4'>Manage Members - {account?.name}</h3>

        {/* Enhanced Email Invite Form */}
        <div className='mb-6 p-4 bg-gray-50 rounded-lg'>
          <div className='flex items-center gap-2 mb-4'>
            <div
              className='flex flex-wrap items-center gap-2 p-2 min-h-[44px] flex-1 border border-gray-200 rounded-lg bg-white cursor-text focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition-all'
              onClick={() => inviteInputRef.current?.focus()}
            >
              {inviteEmails.map((email, index) => (
                <span
                  key={index}
                  className='flex items-center gap-1 px-3 py-1.5 bg-blue-100 rounded-md text-sm text-gray-800'
                >
                  {email}
                  <button
                    type='button'
                    onClick={e => {
                      e.stopPropagation()
                      setInviteEmails(prev => prev.filter((_, i) => i !== index))
                      setInviteSuccess(false)
                    }}
                    className='text-gray-500 hover:text-gray-700 ml-1'
                  >
                    ×
                  </button>
                </span>
              ))}
              <input
                ref={inviteInputRef}
                type='email'
                value={inviteEmailInput}
                onChange={e => setInviteEmailInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault()
                    const email = inviteEmailInput.trim().replace(/,/g, '')
                    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                      if (!inviteEmails.includes(email)) {
                        setInviteEmails(prev => [...prev, email])
                      }
                      setInviteEmailInput('')
                      setInviteSuccess(false)
                    }
                  } else if (
                    e.key === 'Backspace' &&
                    !inviteEmailInput &&
                    inviteEmails.length > 0
                  ) {
                    setInviteEmails(prev => prev.slice(0, -1))
                    setInviteSuccess(false)
                  }
                }}
                onBlur={() => {
                  const email = inviteEmailInput.trim()
                  if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                    if (!inviteEmails.includes(email)) {
                      setInviteEmails(prev => [...prev, email])
                    }
                    setInviteEmailInput('')
                    setInviteSuccess(false)
                  }
                }}
                placeholder={inviteEmails.length === 0 ? 'Enter user email' : ''}
                className='flex-1 min-w-[120px] outline-none text-sm bg-transparent'
              />
            </div>
            <Button
              variant='primary'
              className={`h-[44px] px-5 text-sm font-medium ${
                inviteSuccess ? 'bg-gray-300 text-gray-600 cursor-default' : ''
              }`}
              onClick={handleBulkInvite}
              isDisabled={inviteEmails.length === 0 || isInviting}
            >
              {isInviting ? 'Sending...' : inviteSuccess ? 'Invited' : 'Invite'}
            </Button>
          </div>

          {/* Workspace Selector */}
          <div className='flex items-center justify-between'>
            <span className='text-sm font-medium text-gray-600'>Invite User(s) To</span>
            <div className='relative'>
              <button
                type='button'
                onClick={() => setWorkspaceSelectorOpen(!workspaceSelectorOpen)}
                className='flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200 bg-white'
              >
                <span className='max-w-[200px] truncate'>{getWorkspaceDisplayText()}</span>
                <svg
                  className={`w-4 h-4 transition-transform ${workspaceSelectorOpen ? 'rotate-180' : ''}`}
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M19 9l-7 7-7-7'
                  />
                </svg>
              </button>
              {workspaceSelectorOpen && (
                <div className='absolute right-0 mt-1 w-[280px] bg-white shadow-lg border border-gray-200 rounded-lg z-[110] overflow-hidden'>
                  <div className='max-h-[320px] overflow-y-auto'>
                    {/* As Account Admin option */}
                    <button
                      type='button'
                      onClick={() => {
                        setInviteMode('admin')
                        setSelectedWorkspaces([])
                        setWorkspaceSelectorOpen(false)
                        setInviteSuccess(false)
                      }}
                      className='w-full flex items-center justify-between px-4 py-3 text-sm text-left hover:bg-gray-50 transition-colors'
                    >
                      <span className='font-medium text-gray-800'>As Account Admin</span>
                      {inviteMode === 'admin' && (
                        <svg
                          className='w-5 h-5 text-blue-600'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M5 13l4 4L19 7'
                          />
                        </svg>
                      )}
                    </button>

                    <div className='border-t border-gray-100' />

                    {/* All Workspaces option (excludes Personal Workspaces) */}
                    <button
                      type='button'
                      onClick={() => {
                        setInviteMode('all')
                        setSelectedWorkspaces([])
                        setWorkspaceSelectorOpen(false)
                        setInviteSuccess(false)
                      }}
                      className='w-full flex items-center justify-between px-4 py-3 text-sm text-left hover:bg-gray-50 transition-colors'
                    >
                      <div className='flex flex-col'>
                        <span
                          className={
                            inviteMode === 'all' ? 'font-medium text-blue-600' : 'text-gray-800'
                          }
                        >
                          All Workspaces
                        </span>
                        <span className='text-xs text-gray-400'>Excludes Personal Workspaces</span>
                      </div>
                      {inviteMode === 'all' && (
                        <svg
                          className='w-5 h-5 text-blue-600'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M5 13l4 4L19 7'
                          />
                        </svg>
                      )}
                    </button>

                    {/* Filter out Personal Workspaces - they cannot be shared */}
                    {workspaces.filter(ws => !ws.is_default).length > 0 && (
                      <>
                        <div className='border-t border-gray-100' />
                        <div className='px-4 py-2'>
                          <span className='text-xs font-semibold text-gray-400 uppercase tracking-wider'>
                            to Workspaces
                          </span>
                        </div>
                        {workspaces
                          .filter(ws => !ws.is_default)
                          .map(ws => {
                            const isSelected =
                              inviteMode === 'workspaces' && selectedWorkspaces.includes(ws.org_id)
                            return (
                              <button
                                key={ws.org_id}
                                type='button'
                                onClick={() => {
                                  setInviteMode('workspaces')
                                  setInviteSuccess(false)
                                  if (selectedWorkspaces.includes(ws.org_id)) {
                                    const newSelection = selectedWorkspaces.filter(
                                      id => id !== ws.org_id
                                    )
                                    setSelectedWorkspaces(newSelection)
                                    if (newSelection.length === 0) {
                                      setInviteMode('all')
                                    }
                                  } else {
                                    setSelectedWorkspaces([...selectedWorkspaces, ws.org_id])
                                  }
                                }}
                                className='w-full flex items-center justify-between px-4 py-3 text-sm text-left hover:bg-gray-50 transition-colors'
                              >
                                <span className='text-gray-800'>{ws.org_name}</span>
                                {isSelected && (
                                  <svg
                                    className='w-5 h-5 text-blue-600'
                                    fill='none'
                                    stroke='currentColor'
                                    viewBox='0 0 24 24'
                                  >
                                    <path
                                      strokeLinecap='round'
                                      strokeLinejoin='round'
                                      strokeWidth={2}
                                      d='M5 13l4 4L19 7'
                                    />
                                  </svg>
                                )}
                              </button>
                            )
                          })}
                      </>
                    )}
                    {/* Note about Personal Workspaces */}
                    {workspaces.some(ws => ws.is_default) && (
                      <div className='border-t border-gray-100 px-4 py-2'>
                        <p className='text-xs text-gray-400 italic'>
                          Personal Workspaces are private and cannot be shared.
                        </p>
                      </div>
                    )}
                  </div>
                  {/* Footer buttons */}
                  <div className='border-t border-gray-100 p-3 flex gap-2 justify-end bg-gray-50'>
                    <Button
                      variant='outlined'
                      className='px-4 py-2 text-sm h-8'
                      onClick={() => setWorkspaceSelectorOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant='primary'
                      className='px-4 py-2 text-sm h-8'
                      onClick={() => setWorkspaceSelectorOpen(false)}
                    >
                      Select
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <p className='text-xs text-gray-500 mt-2'>
            If user doesn&apos;t exist, an invitation email will be sent.
          </p>
        </div>

        {/* Members List */}
        <div className='border rounded-lg overflow-hidden'>
          <div className='bg-gray-50 px-4 py-2 border-b font-medium text-sm flex justify-between items-center'>
            <div className='w-4/12'>User</div>
            <div className='w-3/12'>Role</div>
            <div className='w-2/12'>Status</div>
            <div className='w-3/12 text-right'>Actions</div>
          </div>
          <div className='max-h-[300px] overflow-y-auto'>
            {isLoading ? (
              <div className='p-4 text-center text-gray-500 select-none'>Loading members...</div>
            ) : members.length === 0 ? (
              <div className='p-4 text-center text-gray-500'>No members found.</div>
            ) : (
              [...members]
                .sort((a, b) => {
                  // Sort: account_admin first, then by status (active before pending)
                  if (a.is_account_admin !== b.is_account_admin) {
                    return a.is_account_admin ? -1 : 1
                  }
                  if (a.status !== b.status) {
                    return a.status === 'active' ? -1 : 1
                  }
                  return 0
                })
                .map(member => {
                  const isExpired =
                    member.status === 'pending' &&
                    member.expires_at &&
                    new Date(member.expires_at) < new Date()

                  return (
                    <div
                      key={member.user_id || member.invitation_id}
                      className={`px-4 py-3 border-b last:border-0 flex justify-between items-center ${
                        isExpired
                          ? 'bg-red-50'
                          : member.status === 'pending'
                            ? 'bg-yellow-50'
                            : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className='w-4/12 overflow-hidden'>
                        <div className='font-medium text-sm truncate' title={member.email}>
                          {member.email}
                        </div>
                        <div className='text-xs text-gray-500 truncate' title={member.name || ''}>
                          {member.name || '-'}
                        </div>
                      </div>
                      <div className='w-3/12'>
                        {member.is_account_admin ? (
                          <span className='inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700'>
                            Account Admin
                          </span>
                        ) : (
                          <span className='inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600'>
                            Member
                          </span>
                        )}
                      </div>
                      <div className='w-2/12'>
                        {isExpired ? (
                          <span className='inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800'>
                            Expired
                          </span>
                        ) : member.status === 'pending' ? (
                          <span className='inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800'>
                            Pending
                          </span>
                        ) : (
                          <span className='inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800'>
                            Active
                          </span>
                        )}
                      </div>
                      <div className='w-3/12 flex justify-end gap-1'>
                        {member.status === 'pending' && member.invitation_id ? (
                          <>
                            <Button
                              variant='borderless'
                              className='h-7 text-xs px-2'
                              onClick={() => handleResendInvitation(member.invitation_id!)}
                            >
                              {isExpired ? 'Resend' : 'Resend'}
                            </Button>
                            <Button
                              variant='destructive'
                              className='h-7 text-xs px-2 bg-transparent hover:bg-red-50 border-none text-red-600'
                              onClick={() => handleCancelInvitation(member.invitation_id!)}
                            >
                              Cancel
                            </Button>
                          </>
                        ) : member.user_id ? (
                          <Button
                            variant='destructive'
                            className='h-7 text-xs px-2 bg-transparent hover:bg-red-50 border-none text-red-600'
                            onClick={() => handleRemoveMember(member.user_id!)}
                          >
                            Remove
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  )
                })
            )}
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant='outlined' onClick={onClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  )
}

// Workspaces Modal for managing account workspaces
const WorkspacesModal = ({
  isOpen,
  onClose,
  account
}: {
  isOpen: boolean
  onClose: () => void
  account: BillingAccountData | null
}) => {
  const [workspaces, setWorkspaces] = useState<BillingWorkspaceDetails[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [newWorkspaceName, setNewWorkspaceName] = useState('')

  // Edit state
  const [editingWorkspace, setEditingWorkspace] = useState<BillingWorkspaceDetails | null>(null)
  const [editWorkspaceName, setEditWorkspaceName] = useState('')
  const [isUpdating, setIsUpdating] = useState(false)

  // Delete confirmation state
  const [deletingWorkspace, setDeletingWorkspace] = useState<BillingWorkspaceDetails | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Toggle default state
  const [togglingDefaultId, setTogglingDefaultId] = useState<number | null>(null)

  // Members state
  const [expandedWorkspaceId, setExpandedWorkspaceId] = useState<number | null>(null)
  const [workspaceMembers, setWorkspaceMembers] = useState<WorkspaceMember[]>([])
  const [isLoadingMembers, setIsLoadingMembers] = useState(false)
  const [removingMemberId, setRemovingMemberId] = useState<number | null>(null)

  const fetchWorkspaces = React.useCallback(async () => {
    if (!account) return
    try {
      setIsLoading(true)
      const data = await getBillingWorkspacesDetails(account.id)
      setWorkspaces(data)
    } catch {
      BetterToast.error('Failed to fetch workspaces')
    } finally {
      setIsLoading(false)
    }
  }, [account])

  useEffect(() => {
    if (isOpen && account) {
      fetchWorkspaces()
      setNewWorkspaceName('')
      setEditingWorkspace(null)
      setDeletingWorkspace(null)
      setExpandedWorkspaceId(null)
      setWorkspaceMembers([])
    }
  }, [isOpen, account, fetchWorkspaces])

  const handleCreateWorkspace = async () => {
    if (!account || !newWorkspaceName.trim()) return
    try {
      setIsCreating(true)
      await createBillingWorkspace(account.id, { org_name: newWorkspaceName.trim() })
      BetterToast.success('Workspace created successfully')
      setNewWorkspaceName('')
      await fetchWorkspaces()
    } catch (error: unknown) {
      BetterToast.error(error instanceof Error ? error.message : 'Failed to create workspace')
    } finally {
      setIsCreating(false)
    }
  }

  const handleStartEdit = (workspace: BillingWorkspaceDetails) => {
    setEditingWorkspace(workspace)
    setEditWorkspaceName(workspace.org_name)
  }

  const handleCancelEdit = () => {
    setEditingWorkspace(null)
    setEditWorkspaceName('')
  }

  const handleUpdateWorkspace = async () => {
    if (!account || !editingWorkspace || !editWorkspaceName.trim()) return
    try {
      setIsUpdating(true)
      await updateBillingWorkspace(account.id, editingWorkspace.org_id, {
        org_name: editWorkspaceName.trim()
      })
      BetterToast.success('Workspace updated successfully')
      handleCancelEdit()
      await fetchWorkspaces()
    } catch (error: unknown) {
      BetterToast.error(error instanceof Error ? error.message : 'Failed to update workspace')
    } finally {
      setIsUpdating(false)
    }
  }

  const handleConfirmDelete = async () => {
    if (!account || !deletingWorkspace) return
    try {
      setIsDeleting(true)
      await deleteBillingWorkspace(account.id, deletingWorkspace.org_id)
      BetterToast.success('Workspace deleted successfully')
      setDeletingWorkspace(null)
      await fetchWorkspaces()
    } catch (error: unknown) {
      BetterToast.error(error instanceof Error ? error.message : 'Failed to delete workspace')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleToggleDefault = async (workspace: BillingWorkspaceDetails) => {
    if (!account) return
    try {
      setTogglingDefaultId(workspace.org_id)
      await updateBillingWorkspace(account.id, workspace.org_id, {
        is_default: !workspace.is_default
      })
      BetterToast.success(
        workspace.is_default
          ? 'Workspace is no longer personal/default'
          : 'Workspace is now personal/default'
      )
      await fetchWorkspaces()
    } catch (error: unknown) {
      BetterToast.error(error instanceof Error ? error.message : 'Failed to toggle default status')
    } finally {
      setTogglingDefaultId(null)
    }
  }

  const handleToggleMembers = async (workspace: BillingWorkspaceDetails) => {
    if (expandedWorkspaceId === workspace.org_id) {
      setExpandedWorkspaceId(null)
      setWorkspaceMembers([])
      return
    }

    if (!account) return
    try {
      setIsLoadingMembers(true)
      setExpandedWorkspaceId(workspace.org_id)
      const members = await getWorkspaceMembers(account.id, workspace.org_id)
      setWorkspaceMembers(members)
    } catch {
      BetterToast.error('Failed to fetch workspace members')
      setExpandedWorkspaceId(null)
    } finally {
      setIsLoadingMembers(false)
    }
  }

  const handleRemoveWorkspaceMember = async (userId: number) => {
    if (!account || !expandedWorkspaceId) return
    const currentExpandedId = expandedWorkspaceId
    try {
      setRemovingMemberId(userId)
      await removeWorkspaceMember(account.id, currentExpandedId, userId)
      BetterToast.success('Member removed from workspace')
      // Refresh workspaces first to update member count
      await fetchWorkspaces()
      // Then refresh the members list for the currently expanded workspace
      const members = await getWorkspaceMembers(account.id, currentExpandedId)
      setWorkspaceMembers(members)
    } catch (error: unknown) {
      BetterToast.error(error instanceof Error ? error.message : 'Failed to remove member')
    } finally {
      setRemovingMemberId(null)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} hideCloseButton={false} className='!w-[900px]'>
      <ModalBody>
        <h3 className='text-lg font-semibold mb-4'>Manage Workspaces - {account?.name}</h3>

        {/* Create Workspace Form */}
        <div className='mb-6 p-4 bg-gray-50 rounded-lg'>
          <div className='flex items-center gap-2'>
            <div className='flex-1'>
              <Input
                placeholder='Enter workspace name'
                value={newWorkspaceName}
                onChange={e => setNewWorkspaceName(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && newWorkspaceName.trim()) {
                    handleCreateWorkspace()
                  }
                }}
              />
            </div>
            <Button
              variant='primary'
              className='h-10'
              onClick={handleCreateWorkspace}
              isDisabled={!newWorkspaceName.trim() || isCreating}
            >
              {isCreating ? 'Creating...' : 'Create Workspace'}
            </Button>
          </div>
        </div>

        {/* Delete Confirmation */}
        {deletingWorkspace && (
          <div className='mb-4 p-4 border border-red-200 bg-red-50 rounded-lg'>
            <p className='text-sm font-medium text-red-800 mb-2'>
              Are you sure you want to delete &quot;{deletingWorkspace.org_name}&quot;?
            </p>
            <p className='text-xs text-red-600 mb-3'>
              This action will soft-delete the workspace. Members will lose access.
            </p>
            <div className='flex gap-2 justify-end'>
              <Button
                variant='outlined'
                className='h-8 text-xs'
                onClick={() => setDeletingWorkspace(null)}
              >
                Cancel
              </Button>
              <Button
                variant='destructive'
                className='h-8 text-xs'
                onClick={handleConfirmDelete}
                isDisabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Confirm Delete'}
              </Button>
            </div>
          </div>
        )}

        {/* Workspaces List */}
        <div className='border rounded-lg overflow-hidden'>
          <div className='bg-gray-50 px-4 py-2 border-b font-medium text-sm flex items-center'>
            <div className='w-8 flex-shrink-0'></div>
            <div className='flex-1 min-w-0'>Name</div>
            <div className='w-20 flex-shrink-0 text-center'>Members</div>
            <div className='w-20 flex-shrink-0'>Type</div>
            <div className='w-32 flex-shrink-0 text-right'>Actions</div>
          </div>
          <div className='max-h-[500px] overflow-y-auto'>
            {isLoading ? (
              <div className='p-4 text-center text-gray-500 select-none'>Loading workspaces...</div>
            ) : workspaces.length === 0 ? (
              <div className='p-4 text-center text-gray-500'>No workspaces found.</div>
            ) : (
              workspaces.map(workspace => (
                <React.Fragment key={workspace.org_id}>
                  <div
                    className={`px-4 py-3 border-b ${
                      editingWorkspace?.org_id === workspace.org_id
                        ? 'bg-blue-50'
                        : expandedWorkspaceId === workspace.org_id
                          ? 'bg-gray-100'
                          : 'hover:bg-gray-50'
                    }`}
                  >
                    {editingWorkspace?.org_id === workspace.org_id ? (
                      // Edit mode
                      <div className='flex items-center gap-2'>
                        <div className='w-8 flex-shrink-0'></div>
                        <div className='flex-1 min-w-0'>
                          <Input
                            value={editWorkspaceName}
                            onChange={e => setEditWorkspaceName(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter' && editWorkspaceName.trim()) {
                                handleUpdateWorkspace()
                              } else if (e.key === 'Escape') {
                                handleCancelEdit()
                              }
                            }}
                            autoFocus
                          />
                        </div>
                        <Button
                          variant='primary'
                          className='h-8 text-xs px-3 flex-shrink-0'
                          onClick={handleUpdateWorkspace}
                          isDisabled={!editWorkspaceName.trim() || isUpdating}
                        >
                          {isUpdating ? 'Saving...' : 'Save'}
                        </Button>
                        <Button
                          variant='outlined'
                          className='h-8 text-xs px-3 flex-shrink-0'
                          onClick={handleCancelEdit}
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      // View mode
                      <div className='flex items-center'>
                        <div className='w-8 flex-shrink-0'>
                          <button
                            type='button'
                            onClick={() => handleToggleMembers(workspace)}
                            className='p-1 hover:bg-gray-200 rounded'
                          >
                            <svg
                              className={`w-4 h-4 transition-transform ${expandedWorkspaceId === workspace.org_id ? 'rotate-90' : ''}`}
                              fill='none'
                              stroke='currentColor'
                              viewBox='0 0 24 24'
                            >
                              <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M9 5l7 7-7 7'
                              />
                            </svg>
                          </button>
                        </div>
                        <div className='flex-1 min-w-0 pr-2'>
                          <div className='flex items-center gap-2'>
                            <span
                              className='font-medium text-sm truncate'
                              title={workspace.org_name}
                            >
                              {workspace.org_name}
                            </span>
                            <Switch
                              size='sm'
                              isSelected={workspace.is_default}
                              isDisabled={togglingDefaultId !== null}
                              onValueChange={() => handleToggleDefault(workspace)}
                              aria-label='Toggle personal workspace'
                            />
                            <Chip
                              size='sm'
                              variant='flat'
                              color={workspace.is_default ? 'secondary' : 'default'}
                              className='text-xs'
                            >
                              {workspace.is_default ? 'Personal' : 'Shared'}
                            </Chip>
                          </div>
                          <div className='text-xs text-gray-500 truncate' title={workspace.uuid}>
                            UUID: {workspace.uuid.slice(0, 8)}...
                          </div>
                        </div>
                        <div className='w-20 flex-shrink-0 text-center'>
                          <span className='inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800'>
                            {workspace.member_count}
                          </span>
                        </div>
                        <div className='w-20 flex-shrink-0'>
                          <span className='text-xs text-gray-600'>{workspace.org_type}</span>
                        </div>
                        <div className='w-32 flex-shrink-0 flex justify-end gap-1'>
                          <Button
                            variant='borderless'
                            className='h-7 text-xs px-2'
                            onClick={() => handleStartEdit(workspace)}
                            isDisabled={deletingWorkspace !== null}
                          >
                            Edit
                          </Button>
                          <Button
                            variant='destructive'
                            className='h-7 text-xs px-2 bg-transparent hover:bg-red-50 border-none text-red-600'
                            onClick={() => setDeletingWorkspace(workspace)}
                            isDisabled={deletingWorkspace !== null || workspace.is_default}
                            title={
                              workspace.is_default
                                ? 'Personal Workspace cannot be deleted'
                                : undefined
                            }
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Expanded members section */}
                  {expandedWorkspaceId === workspace.org_id && (
                    <div className='bg-gray-50 border-b'>
                      {isLoadingMembers ? (
                        <div className='p-4 text-center text-gray-500 text-sm'>
                          Loading members...
                        </div>
                      ) : workspaceMembers.length === 0 ? (
                        <div className='p-4 text-center text-gray-500 text-sm'>
                          No members found.
                        </div>
                      ) : (
                        <div className='p-3'>
                          <div className='text-xs font-semibold text-gray-600 mb-2 px-2'>
                            Members ({workspaceMembers.length})
                          </div>
                          <div className='space-y-1'>
                            {workspaceMembers
                              .sort((a, b) => {
                                // Sort: account_admin first, then admin, then user
                                const priority = { account_admin: 0, admin: 1, user: 2 }
                                return (priority[a.role] ?? 3) - (priority[b.role] ?? 3)
                              })
                              .map(member => (
                                <div
                                  key={member.user_id}
                                  className='flex items-center justify-between px-3 py-2 bg-white rounded border text-sm'
                                >
                                  <div className='flex-1 min-w-0'>
                                    <div className='flex items-center gap-2'>
                                      <span className='font-medium truncate'>{member.email}</span>
                                      {member.is_account_admin ? (
                                        <span className='text-xs px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 flex-shrink-0'>
                                          Account Admin
                                        </span>
                                      ) : (
                                        <span className='text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 flex-shrink-0'>
                                          {member.role === 'admin' ? 'Workspace Admin' : 'Member'}
                                        </span>
                                      )}
                                    </div>
                                    <div className='text-xs text-gray-500'>
                                      {member.name || 'No name'}
                                    </div>
                                  </div>
                                  {/* Remove button - only for non-account admins (account admins are tied to the account, not removable) */}
                                  {!member.is_account_admin && (
                                    <Button
                                      variant='destructive'
                                      className='h-6 text-xs px-2 bg-transparent hover:bg-red-50 border-none text-red-600 flex-shrink-0'
                                      onClick={() => handleRemoveWorkspaceMember(member.user_id)}
                                      isDisabled={removingMemberId === member.user_id}
                                      title='Remove from workspace'
                                    >
                                      {removingMemberId === member.user_id
                                        ? 'Removing...'
                                        : 'Remove'}
                                    </Button>
                                  )}
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </React.Fragment>
              ))
            )}
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant='outlined' onClick={onClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  )
}

function AccountManagement() {
  const [searchParams, setSearchParams] = useSearchParams()

  // State - initialize from URL params if present
  const [billingAccounts, setBillingAccounts] = useState<BillingAccountData[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [filterValue, setFilterValue] = useState(() => searchParams.get('filter') || '')
  const [page, setPage] = useState(() => {
    const p = searchParams.get('page')
    return p ? parseInt(p, 10) : 1
  })
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const r = searchParams.get('rows')
    return r ? parseInt(r, 10) : 20
  })
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>(() => ({
    column: searchParams.get('sort') || 'created_time',
    direction: (searchParams.get('dir') as 'ascending' | 'descending') || 'descending'
  }))

  // Modal states using existing useModal hook
  const createModal = useModal()
  const editModal = useModal()
  const deleteModal = useModal()
  const membersModal = useModal()
  const detailModal = useModal()
  const licensesModal = useModal()
  const workspacesModal = useModal()
  const creditsModal = useModal()
  const usageModal = useModal()

  // Form state
  const [formData, setFormData] = useState<FormData>(initialFormData)
  const [selectedAccount, setSelectedAccount] = useState<BillingAccountData | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const columns: Column[] = [
    { name: 'ID', uid: 'id', sortable: true },
    { name: 'Name', uid: 'name', sortable: true },
    { name: 'Licenses', uid: 'license_types', sortable: false },
    { name: 'Note', uid: 'note', sortable: false },
    { name: 'Actions', uid: 'actions' }
  ]

  // Fetch data
  const fetchBillingAccounts = useCallback(async () => {
    try {
      setIsLoading(true)
      const accounts = await getBillingAccounts()
      setBillingAccounts(accounts)
    } catch {
      BetterToast.error('Failed to fetch billing accounts')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchBillingAccounts()
  }, [fetchBillingAccounts])

  // Sync state to URL query params
  useEffect(() => {
    const params = new URLSearchParams()

    // Only set non-default values
    if (filterValue) params.set('filter', filterValue)
    if (page !== 1) params.set('page', String(page))
    if (rowsPerPage !== 20) params.set('rows', String(rowsPerPage))
    if (sortDescriptor.column && sortDescriptor.column !== 'created_time') {
      params.set('sort', String(sortDescriptor.column))
    }
    if (sortDescriptor.direction && sortDescriptor.direction !== 'descending') {
      params.set('dir', sortDescriptor.direction)
    }

    // Update URL without navigation (replace to avoid polluting history)
    setSearchParams(params, { replace: true })
  }, [filterValue, page, rowsPerPage, sortDescriptor, setSearchParams])

  // CRUD handlers
  const handleCreate = async () => {
    if (!formData.name) {
      BetterToast.error('Name is required')
      return
    }

    try {
      setIsSubmitting(true)
      await createBillingAccount({
        name: formData.name,
        note: formData.note || undefined
      })
      BetterToast.success('Billing account created successfully')
      createModal.close()
      setFormData(initialFormData)
      await fetchBillingAccounts()
    } catch (error: unknown) {
      BetterToast.error(error instanceof Error ? error.message : 'Failed to create billing account')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleEdit = async () => {
    if (!selectedAccount) return

    try {
      setIsSubmitting(true)
      await updateBillingAccount(selectedAccount.id, {
        id: selectedAccount.id,
        name: formData.name,
        note: formData.note
      })
      BetterToast.success('Billing account updated successfully')
      editModal.close()
      setSelectedAccount(null)
      setFormData(initialFormData)
      await fetchBillingAccounts()
    } catch (error: unknown) {
      BetterToast.error(error instanceof Error ? error.message : 'Failed to update billing account')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!selectedAccount) return

    try {
      setIsSubmitting(true)
      await deleteBillingAccount(selectedAccount.id)
      BetterToast.success('Billing account deleted successfully')
      deleteModal.close()
      setSelectedAccount(null)
      await fetchBillingAccounts()
    } catch (error: unknown) {
      BetterToast.error(error instanceof Error ? error.message : 'Failed to delete billing account')
    } finally {
      setIsSubmitting(false)
    }
  }

  const openEditModal = (account: BillingAccountData) => {
    setSelectedAccount(account)
    setFormData({
      name: account.name || '',
      note: account.note || ''
    })
    editModal.open()
  }

  const openDeleteModal = (account: BillingAccountData) => {
    setSelectedAccount(account)
    deleteModal.open()
  }

  const openMembersModal = (account: BillingAccountData) => {
    setSelectedAccount(account)
    membersModal.open()
  }

  const openDetailModal = (account: BillingAccountData) => {
    setSelectedAccount(account)
    detailModal.open()
  }

  const openLicensesModal = (account: BillingAccountData) => {
    setSelectedAccount(account)
    licensesModal.open()
  }

  const openWorkspacesModal = (account: BillingAccountData) => {
    setSelectedAccount(account)
    workspacesModal.open()
  }

  const openCreditsModal = (account: BillingAccountData) => {
    setSelectedAccount(account)
    creditsModal.open()
  }

  const openUsageModal = (account: BillingAccountData) => {
    setSelectedAccount(account)
    usageModal.open()
  }

  // Filtering and pagination
  const filteredItems = React.useMemo(() => {
    if (!filterValue) return billingAccounts
    const lower = filterValue.toLowerCase()
    return billingAccounts.filter(
      item =>
        item.name?.toLowerCase().includes(lower) ||
        item.note?.toLowerCase().includes(lower) ||
        item.id?.toString().includes(lower)
    )
  }, [billingAccounts, filterValue])

  const pages = Math.max(1, Math.ceil(filteredItems.length / rowsPerPage))

  const items = React.useMemo(() => {
    const start = (page - 1) * rowsPerPage
    const end = start + rowsPerPage
    return filteredItems.slice(start, end)
  }, [page, filteredItems, rowsPerPage])

  // Render cell
  const renderCell = (item: BillingAccountData, columnKey: string) => {
    switch (columnKey) {
      case 'id':
        return <span className='text-xs text-default-400 font-mono'>#{item.id}</span>
      case 'name':
        return (
          <div className='flex items-center gap-2'>
            <span
              className={`text-sm font-semibold ${
                item.deleted ? 'text-default-400 line-through' : 'text-default-900'
              }`}
            >
              {item.name}
            </span>
            {item.deleted && (
              <span
                className='inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700'
                title={item.deleted_at ? `Soft-deleted at ${item.deleted_at}` : 'Soft-deleted'}
              >
                Deleted
              </span>
            )}
          </div>
        )
      case 'license_types':
        return (
          <div className='flex flex-wrap gap-1'>
            {item.license_types?.length > 0 ? (
              item.license_types.map(label => (
                <span
                  key={label}
                  className='inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700'
                >
                  {label}
                </span>
              ))
            ) : (
              <span className='text-xs text-default-400'>-</span>
            )}
          </div>
        )
      case 'note':
        return (
          <div className='max-w-[400px]'>
            <p className='text-sm text-default-500 truncate' title={item.note || ''}>
              {item.note || '-'}
            </p>
          </div>
        )
      case 'actions':
        return (
          <div className='flex gap-2 justify-end'>
            <Button
              variant='borderless'
              className='h-8 px-3 text-sm'
              onClick={() => openDetailModal(item)}
            >
              Details
            </Button>
            <Button
              variant='borderless'
              className='h-8 px-3 text-sm'
              onClick={() => openMembersModal(item)}
            >
              Members
            </Button>
            <Button
              variant='borderless'
              className='h-8 px-3 text-sm'
              onClick={() => openWorkspacesModal(item)}
            >
              Workspaces
            </Button>
            <Button
              variant='borderless'
              className='h-8 px-3 text-sm'
              onClick={() => openLicensesModal(item)}
            >
              Licenses
            </Button>
            <Button
              variant='borderless'
              className='h-8 px-3 text-sm'
              onClick={() => openCreditsModal(item)}
            >
              Credits
            </Button>
            <Button
              variant='borderless'
              className='h-8 px-3 text-sm'
              onClick={() => openUsageModal(item)}
            >
              Usage
            </Button>
            <Button
              variant='borderless'
              className='h-8 px-3 text-sm'
              onClick={() => openEditModal(item)}
            >
              Rename
            </Button>
            <CustomTooltip
              content={
                item.deleted
                  ? 'Account is already soft-deleted.'
                  : !item.can_delete
                    ? `Cannot delete: Account has ${item.member_count} member(s). Remove all members first.`
                    : 'Delete account'
              }
            >
              <Button
                variant='destructive'
                className='h-8 px-3 text-sm bg-transparent hover:bg-red-50 border-none'
                onClick={() => openDeleteModal(item)}
                isDisabled={!item.can_delete || !!item.deleted}
              >
                Delete
              </Button>
            </CustomTooltip>
          </div>
        )
      default:
        return null
    }
  }

  // Form component
  const renderForm = () => (
    <div className='flex flex-col gap-4'>
      <Input
        label='Name *'
        placeholder='Enter account name'
        value={formData.name}
        onChange={e => setFormData({ ...formData, name: e.target.value })}
      />
      <div>
        <label className='text-sm font-medium text-tokens-neutral-text-primary mb-1 block'>
          Note
        </label>
        <SimpleTextarea
          placeholder='Enter admin notes for this account'
          value={formData.note}
          onChange={value => setFormData({ ...formData, note: value })}
          minRows={3}
          maxRows={6}
        />
      </div>
    </div>
  )

  return (
    <Card className='w-full'>
      <CardHeader className='flex justify-between items-center'>
        <h2 className='text-xl font-bold'>Billing Account Management</h2>
        <Button
          variant='primary'
          className='h-8 text-sm'
          onClick={() => {
            setFormData(initialFormData)
            createModal.open()
          }}
        >
          + Create Account
        </Button>
      </CardHeader>
      <CardBody>
        <DataTable
          items={items}
          columns={columns}
          page={page}
          pages={pages}
          rowsPerPage={rowsPerPage}
          filterValue={filterValue}
          sortDescriptor={sortDescriptor}
          isLoading={isLoading}
          onSearchChange={value => {
            setFilterValue(value)
            setPage(1)
          }}
          onClear={() => {
            setFilterValue('')
            setPage(1)
          }}
          onRowsPerPageChange={setRowsPerPage}
          onPageChange={setPage}
          onSortChange={setSortDescriptor}
          renderCell={renderCell}
          searchPlaceholder='Search by name or note...'
          totalCount={filteredItems.length}
        />
      </CardBody>

      {/* Create Modal - using existing Modal component */}
      <Modal isOpen={createModal.isOpen} onClose={createModal.close} hideCloseButton={false}>
        <ModalBody>
          <h3 className='text-lg font-semibold mb-4'>Create Billing Account</h3>
          {renderForm()}
        </ModalBody>
        <ModalFooter>
          <Button variant='outlined' onClick={createModal.close}>
            Cancel
          </Button>
          <Button variant='primary' onClick={handleCreate} isDisabled={isSubmitting}>
            {isSubmitting ? 'Creating...' : 'Create'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={editModal.isOpen} onClose={editModal.close} hideCloseButton={false}>
        <ModalBody>
          <h3 className='text-lg font-semibold mb-4'>Rename Account</h3>
          {renderForm()}
        </ModalBody>
        <ModalFooter>
          <Button variant='outlined' onClick={editModal.close}>
            Cancel
          </Button>
          <Button variant='primary' onClick={handleEdit} isDisabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal.isOpen} onClose={deleteModal.close} hideCloseButton={false}>
        <ModalBody>
          <h3 className='text-lg font-semibold mb-2'>Delete Billing Account</h3>
          <p>
            Are you sure you want to delete the billing account{' '}
            <strong>{selectedAccount?.name}</strong>?
          </p>
          <p className='text-sm text-gray-500 mt-2'>
            This action cannot be undone. All associated data will be soft-deleted.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button variant='outlined' onClick={deleteModal.close}>
            Cancel
          </Button>
          <Button variant='destructive' onClick={handleDelete} isDisabled={isSubmitting}>
            {isSubmitting ? 'Deleting...' : 'Delete'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Members Management Modal */}
      <MembersModal
        isOpen={membersModal.isOpen}
        onClose={membersModal.close}
        account={selectedAccount}
      />

      {/* Detail Modal */}
      <DetailModal
        isOpen={detailModal.isOpen}
        onClose={detailModal.close}
        account={selectedAccount}
      />

      {/* Licenses Modal */}
      <LicensesModal
        isOpen={licensesModal.isOpen}
        onClose={licensesModal.close}
        account={selectedAccount}
        onUpdate={fetchBillingAccounts}
      />

      {/* Workspaces Modal */}
      <WorkspacesModal
        isOpen={workspacesModal.isOpen}
        onClose={workspacesModal.close}
        account={selectedAccount}
      />

      {/* Credits Management Modal */}
      <CreditsModal
        isOpen={creditsModal.isOpen}
        onClose={creditsModal.close}
        account={selectedAccount}
      />

      {/* Full Manage Account (all tabs): same as product Manage Account dialog */}
      {selectedAccount && (
        <SuperAdminManageAccountDialog
          key={selectedAccount.id}
          isOpen={usageModal.isOpen}
          onClose={usageModal.close}
          billingAccountId={selectedAccount.id}
        />
      )}
    </Card>
  )
}

export default AccountManagement
